import { useEffect, useRef, useState } from 'react';
import { useTheme } from '../../../context/ThemeContext';

interface FinalCTASectionProps {
  onOpenModal: (type: 'demo' | 'start') => void;
}

export default function FinalCTASection({ onOpenModal }: FinalCTASectionProps) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);
  const { isDark } = useTheme();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className={`relative py-32 md:py-44 overflow-hidden ${isDark ? 'bg-[#080808]' : 'bg-white'}`}>
      <div className={`absolute top-0 left-0 right-0 h-px ${isDark ? 'bg-[#1A1A1A]' : 'bg-[#E8E8ED]'}`}></div>

      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/78f1ea169f566d228d3887ac051c50c9.jpg"
          alt=""
          className={`w-full h-full object-cover transition-opacity duration-500 ${isDark ? 'opacity-15' : 'opacity-5'}`}
        />
        <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-b from-[#080808] via-[#080808]/50 to-[#080808]' : 'bg-gradient-to-b from-white via-white/80 to-white'}`}></div>
      </div>

      {/* Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-[#FF2B2B]/5 blur-3xl pointer-events-none"></div>

      <div
        className="relative z-10 max-w-4xl mx-auto px-6 text-center"
        style={{ opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(40px)', transition: 'all 0.9s cubic-bezier(0.16,1,0.3,1)' }}
      >
        <div className="inline-flex items-center gap-2 bg-[#FF2B2B]/10 border border-[#FF2B2B]/20 px-4 py-1.5 rounded-full mb-10">
          <span className="w-1.5 h-1.5 rounded-full bg-[#FF2B2B] animate-glow-pulse inline-block"></span>
          <span className="text-[#FF2B2B] text-xs font-semibold tracking-widest uppercase">Ready to Start?</span>
        </div>

        <h2
          className={`text-4xl md:text-6xl lg:text-8xl font-black leading-tight mb-8 ${isDark ? 'text-white' : 'text-[#1D1D1F]'}`}
          style={{ letterSpacing: '-0.04em' }}
        >
          Upgrade the Way<br />
          Your Business <span className="text-[#FF2B2B]">Responds.</span>
        </h2>

        <p className={`text-lg md:text-2xl max-w-2xl mx-auto mb-14 leading-relaxed ${isDark ? 'text-[#C9CED6]/60' : 'text-[#6E6E73]'}`} style={{ fontWeight: 300 }}>
          A bot smarter. Built to capture what you&apos;re currently missing.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-5">
          <button
            onClick={() => onOpenModal('demo')}
            className="group bg-[#FF2B2B] hover:bg-[#e02222] text-white font-bold px-12 py-5 rounded-xl text-base transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center gap-2"
            style={{ boxShadow: '0 12px 40px rgba(255,43,43,0.35)' }}
          >
            Schedule a Demo
            <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
          </button>
          <button
            onClick={() => onOpenModal('start')}
            className={`font-semibold px-12 py-5 rounded-xl text-base transition-all duration-300 cursor-pointer whitespace-nowrap border ${
              isDark ? 'border-[#2A2A2A] text-[#C9CED6] hover:border-[#C9CED6]/30' : 'border-[#D2D2D7] text-[#1D1D1F] hover:bg-[#F5F5F7]'
            }`}
          >
            Get Started
          </button>
        </div>
      </div>
    </section>
  );
}
